import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  MenuList,
  MenuItem,
  Menu,
  MenuButton,
  IconButton,
} from "@chakra-ui/react";
import assetTypeToolTip from "pages/Analytics/elements/AmountStatusBar/assetTypeToolTip";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { NoDataAvailable } from "../NoDataAvailable";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import { WidgetBoxActions } from "components/elements/WidgetBox/widget-box-actions";

const LOAN_TYPE_COLORS = [
  "#F87171",
  "#FB923C",
  "#FACC15",
  "#A3E635",
  "#38BDF8",
  "#818CF8",
  "#E879F9",
  "#FB7185",
  "#FCA5A5",
  "#FEF08A",
  "#99F6E4",
  "#BFDBFE",
  "#E9D5FF",
  "#FECDD3",
];

const formatEntryName = (entryValue: string) => {
  const words = entryValue.split(/\s+|(?<=\/)/);
  const capitalizedWords = words.map(
    word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  return capitalizedWords.join(" ");
};

const CustomLegend: React.FC<{ data: any[] }> = ({ data }) => {
  return (
    <VStack align="stretch" spacing={1} mt={2} maxHeight="140px" overflowY="auto">
      {data.map((entry, index) => (
        <HStack key={`item-${index}`} justifyContent="space-between">
          <HStack>
            <Box
              bg={LOAN_TYPE_COLORS[index % LOAN_TYPE_COLORS.length]}
              width="10px"
              height="10px"
              borderRadius="sm"
              mr={1}
            />
            <Text
              color="#171B1E"
              fontWeight="500"
              fontSize="12px"
              overflow={"hidden"}
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              height={"16px"}
            >
              {formatEntryName(entry.name)}
            </Text>
          </HStack>
          <Text
            color="#171B1E"
            fontWeight="500"
            fontSize="12px"
            align={"right"}
            overflow={"hidden"}
            height={"16px"}
            whiteSpace={"nowrap"}
            textOverflow={"ellipsis"}
          >
            {entry.value}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
};

const AssertTypeBreakDown: React.FC = () => {
  const { loans } = useContext(LoansContext);
  const { user } = useTenant();
  const [assetTypesData, setAssetTypesData] = useState<any[]>([]);
  const widgetBoxRef = useRef<HTMLDivElement>(null);

  const assetsField = ["Asset Type", "Property Type"];

  let title: string = "Asset Type Breakdown";

  if (user?.company?.fields?.find((field: any) => field?.label === assetsField[1])) {
    title = "Property Type Breakdown";
  }

  const loadAssetTypes = () => {
    const assetTypeField = user?.company?.fields?.find((field: any) =>
      assetsField.includes(field?.label)
    );

    const assetTypes = assetTypeField?.options || [];

    const assetTypeValuesFromLoans =
      loans?.flatMap((loan: any) =>
        (loan?.values || [])
          .filter(
            (value: any) =>
              assetsField.includes(value?.loan_field?.label) && value?.value?.field !== null
          )
          .map((value: any) => value?.value?.field)
      ) || [];

    const loanCountsPerAssetType = {} as any;

    assetTypes.forEach((assetType: any) => {
      if (assetType) {
        const normalizedValue = assetType.trim().toLowerCase();
        loanCountsPerAssetType[normalizedValue] = 0;
      }
    });

    assetTypeValuesFromLoans.forEach((value: any) => {
      if (value) {
        const normalizedValue = value.value ?? value.trim().toLowerCase();
        if (normalizedValue) {
          loanCountsPerAssetType[normalizedValue] =
            (loanCountsPerAssetType[normalizedValue] || 0) + 1;
        }
      }
    });

    const assetTypesData = Object.entries(loanCountsPerAssetType)
      .filter(([name]) => name)
      .map(([name, value]) => ({
        name,
        value,
      }));

    setAssetTypesData(assetTypesData);
  };

  useEffect(() => {
    loadAssetTypes();
  }, [loans]);

  const handleExport = () => {
    if (widgetBoxRef.current) {
      const exportElement = document.getElementById("hide");
      if (exportElement) {
        exportElement.style.display = "none";
      }
      html2canvas(widgetBoxRef.current, { scale: 2 }).then(canvas => {
        const imageURL = canvas.toDataURL();
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "asset_type_breakdown.png";
        link.click();
        if (exportElement) {
          exportElement.style.display = "block";
        }
      });
    }
  };

  if (assetTypesData.length === 0) {
    return <NoDataAvailable title={title} />;
  }

  return (
    <WidgetBox title={title}>
      <WidgetBoxActions>
        <Menu>
          <MenuButton
            as={IconButton}
            variant="ghost"
            size={"sm"}
            color="gray.600"
            _hover={{
              color: "primary",
            }}
            aria-label="document-actions"
            icon={<FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />}
          />
          <MenuList>
            <MenuItem onClick={handleExport}>Export</MenuItem>
          </MenuList>
        </Menu>
      </WidgetBoxActions>
      <Flex direction="row" gap={4} height={"200px"}>
        <Box flex={1}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={assetTypesData}
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="80%"
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
              >
                {assetTypesData.map((_entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={LOAN_TYPE_COLORS[index % LOAN_TYPE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={props => assetTypeToolTip(props, { user: user })} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box flex={1} alignContent={"center"}>
          <CustomLegend data={assetTypesData} />
        </Box>
      </Flex>
    </WidgetBox>
  );
};

export default AssertTypeBreakDown;
